import { Box, Chip, Theme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import * as H from 'history';
import moment from 'moment';
import React from 'react';
import { generatePath } from 'react-router';
import { Link, Route, Switch, useLocation, useParams } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { IUserAtom, IUserModel, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { AgentDropdown } from '../components/dropdowns/AgentsDropdown';
import { DevicesAndRouting } from '../subpages/DevicesAndRouting';
import { Numbers } from '../subpages/Numbers';
import { SmsTemplates } from '../subpages/SmsTemplates';
import { VmDrop } from '../subpages/VmDrop';
import { AgentPowerlists } from './AgentPowerlists';
import { EditAgentSettings } from './EditAgentSettings';
import { PowerlistContacts } from './PowerlistContacts';
import { makeNewAgentRoute } from './utils/makeNewRoute';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #515151' : '1px solid #e0e0e0',
    indicator: {
      backgroundColor: '#0098C7',
    },
  },
  tab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&[aria-selected=true]': {
      color: '#0098C7',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  paper: {
    flexGrow: 1,
  },
  tabTitle: {
    display: 'flex',
    paddingTop: 15,
    justifyContent: 'space-between',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  line: {
    margin: '5px 20px',
    backgroundColor: '#2B7DE4',
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  powerlistContactBody: {
    '& [aria-label=statsToolbar]': {
      marginRight: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
  },
  editAgentBtn: {
    display: 'none',
  },
}));

interface IViewAgentTabs {
  history: H.History;
  basePath: string;
}

export function ViewAgentTabs({
  history,
  basePath = '/manage/agents/:id',
}: IViewAgentTabs) {
  const { id } = useParams<{ id: string }>();
  const userLoadable = useRecoilValueLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;
  const [_, businessDataContents] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessData =
    businessDataContents?.contents?.response?.data?.result?.[0];

  const setRefetchToggleForbusinessData = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DATA)
  );

  const [
    businessUserCurrentCached,
    businessUserCurrentLoadable,
  ] = makeApiRequestCached<IUserModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, _cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])?.find(
        (user) => user.userid === +id
      )
  );

  if (basePath.startsWith('/my-profile/')) {
    /*
     * If the URL is my-profile then for security reasons we ensure the ID in the URL is loggedin users ID
     * If not we need to reset the id in agentUserId Atom and we redirect back to my profile with correct ID
     */
    const userId = userContents?.user?.userid;

    if (userId && id !== String(userId)) {
      history.push(generatePath(basePath, { id: userId }));
    }
  }

  const { canAccessPowerlistProduct } = useRecoilValueLoadable<IPermissions>(
    permissionsSelector
  ).contents as IPermissions;

  const pages = {
    devicesAndRouting: {
      pathname: `${basePath}/devices-and-routing`,
      showCalendar: false,
    },
    Numbers: {
      pathname: `${basePath}/numbers`,
    },
    Edit: {
      pathname: `${basePath}/edit`,
    },
    vmDrop: {
      pathname: `${basePath}/vm-drop`,
    },
    smsTemplates: {
      pathname: `${basePath}/sms-template-help`,
    },
    powerlists: {
      pathname: `${basePath}/powerlists`,
    },
    powerlistContacts: {
      pathname: `${basePath}/powerlists/:powerlistId/view-contacts`,
    },
  };
  const classes = useStyles();
  const location = useLocation();
  const myProfilePage = basePath.startsWith('/my-profile/');
  return (
    <>
      <Paper className={classes.paper}>
        {businessDataContents.state === 'hasValue' &&
          location.pathname !== generatePath(basePath, { id }) &&
          location.pathname !== `${generatePath(basePath, { id })}/` && ( // on initial load prevent render since basePath tab undefined
            <Box display="flex" justifyContent="center" width="100%">
              <Tabs
                className={classes.tabs}
                value={
                  location?.pathname?.includes('powerlists')
                    ? generatePath(pages.powerlists.pathname, { id })
                    : location.pathname.split('/threaded-sms/')[0]
                } // ignore after threaded-smsore after threaded-sms
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event: React.ChangeEvent<any>, _value: any) => {
                  setRefetchToggleForbusinessData({});
                }}
                variant="scrollable"
              >
                <Tab
                  className={classes.tab}
                  label="Agent Settings"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.Edit.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.Edit.pathname, { id }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-edit"
                />
                <Tab
                  className={classes.tab}
                  label="Inbound Call Flow"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.devicesAndRouting.pathname, {
                    id,
                  })}
                  to={{
                    pathname: generatePath(pages.devicesAndRouting.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-devices-and-routing"
                />
                <Tab
                  className={classes.tab}
                  label="Numbers"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.Numbers.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.Numbers.pathname, { id }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-numbers"
                />
                {!!businessData?.allowAgentPowerlist &&
                  canAccessPowerlistProduct && (
                    <Tab
                      className={classes.tab}
                      label="PowerLists"
                      disableRipple
                      component={Link}
                      value={generatePath(pages.powerlists.pathname, {
                        id,
                      })}
                      to={{
                        pathname: generatePath(pages.powerlists.pathname, {
                          id,
                        }),
                        search: location.search,
                      }}
                      data-pup="view-agent-tab-powerlists"
                    />
                  )}
                <Tab
                  className={classes.tab}
                  label="SMS Templates"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.smsTemplates.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.smsTemplates.pathname, {
                      id,
                    }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-sms-templates"
                />
                <Tab
                  className={classes.tab}
                  label="VM Drop"
                  disableRipple
                  component={Link}
                  value={generatePath(pages.vmDrop.pathname, { id })}
                  to={{
                    pathname: generatePath(pages.vmDrop.pathname, { id }),
                    search: location.search,
                  }}
                  data-pup="view-agent-tab-vm-drop"
                />
              </Tabs>
            </Box>
          )}

        <header className={classes.tabTitle}>
          <div style={{ display: 'flex', alignItems: 'end' }}>
            {!myProfilePage && (
              <AgentDropdown
                userId={id}
                handleChange={(newValue) => {
                  if (!newValue?.userid) return;
                  history.push(
                    makeNewAgentRoute(id, newValue.userid, location.pathname)
                  );
                }}
              />
            )}
            <h2 style={{ position: 'relative', top: '3px' }}>
              {myProfilePage && <>My Profile</>}
            </h2>
          </div>
          <aside>
            <span>
              {Boolean(businessUserCurrentCached.current?.created) && (
                <Chip
                  className={classes.chip}
                  label={moment(
                    businessUserCurrentCached.current?.created
                  ).format('YYYY-MM-DD')}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.userTier) && (
                <Chip
                  className={classes.chip}
                  label={
                    businessUserCurrentCached.current?.userTier === 'Enterprise'
                      ? 'Professional'
                      : businessUserCurrentCached.current?.userTier ===
                        'Enterprise Unlimited'
                      ? 'Professional Unlimited'
                      : businessUserCurrentCached.current?.userTier
                  }
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.crmname) && (
                <Chip
                  className={classes.chip}
                  label={businessUserCurrentCached.current?.crmname}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
            <span>
              {Boolean(businessUserCurrentCached.current?.smsDID) && (
                <Chip
                  className={classes.chip}
                  label={businessUserCurrentCached.current?.smsDID}
                  variant="outlined"
                  size="small"
                />
              )}
            </span>
          </aside>
        </header>

        <Switch>
          <Route
            path={pages.Numbers.pathname}
            render={() => {
              const currentAgent = (businessUserCurrentLoadable.contents
                ?.response?.data?.result as IUserModel[])?.find(
                (user) => user.userid === +id
              );
              return <Numbers userTier={currentAgent?.userTier} />;
            }}
          />
          <Route path={pages.vmDrop.pathname} render={() => <VmDrop />} />
          <Route
            path={pages.smsTemplates.pathname}
            render={() => <SmsTemplates agentId={id} />}
          />
          <Route
            path={pages.powerlistContacts.pathname}
            render={() => (
              <div className={classes.powerlistContactBody}>
                <PowerlistContacts
                  powerlistId={userContents?.selectedPowerlistId}
                />
              </div>
            )}
          />
          <Route
            path={pages.powerlists.pathname}
            render={() => (
              <AgentPowerlists
                profile={basePath.startsWith('/my-profile/')}
                agentId={id}
              />
            )}
          />
        </Switch>
      </Paper>
      <Switch>
        <Route
          path={pages.Edit.pathname}
          render={(props) => (
            <EditAgentSettings
              {...props}
              userId={id}
              businessId={businessData?.businessid}
            />
          )}
        />
        <Route
          path={pages.devicesAndRouting.pathname}
          render={(props) => <DevicesAndRouting {...props} id={id} />}
        />
      </Switch>
    </>
  );
}
